import classNames from 'classnames';
import { X } from 'lucide-react';
import React, { useState, ReactElement, useRef, ChangeEvent, useEffect } from 'react';
import { alphanumeric, numbers_dot, numbers_letters_dot_comma, numbers_only } from '../utils/validate';

export default function Textarea({
	name,
	placeholder,
	value = '',
	readonly = false,
	disabled,
	allowPaste = true,
	validation,
	transformation,
	allowedChars,
	clearable = true,
	maxLength,
	rows = 4,
	resize = 'none',
	onChange,
	onSubmit,
	onKeyDown,
	className = '',
	containerClassName = '',
	textareaClassName = '',
	Icon,
	title,
	floatingLabel,
}: {
	name?: string;
	placeholder: string;
	value?: string;
	readonly?: boolean;
	disabled?: boolean;
	allowPaste?: boolean;
	validation?: (value: string) => boolean;
	transformation?: (value: string) => string;
	allowedChars?: 'numeric' | 'alphanumeric' | 'numbers_only' | 'numbers_letters_dot_comma';
	clearable?: boolean;
	maxLength?: number;
	rows?: number;
	resize?: 'none' | 'both' | 'horizontal' | 'vertical';
	onSubmit?: (value: string) => void;
	onChange?: (value: string) => void;
	onKeyDown?: (event: React.KeyboardEvent<HTMLTextAreaElement>) => void;
	className?: string;
	containerClassName?: string;
	textareaClassName?: string;
	Icon?: ReactElement;
	title?: string;
	floatingLabel?: string;
}) {
	const [currentValue, setCurrentValue] = useState<string>(value);
	const textareaEl = useRef<null | HTMLTextAreaElement>(null);

	useEffect(() => {
		setCurrentValue(value);
	}, [value]);

	const [isValid, setValid] = useState(true);

	const handleOnChange = (event: ChangeEvent<HTMLTextAreaElement>) => {
		let newValue = event.target.value;

		// Remove disallowed chars
		if (allowedChars) {
			newValue =
				allowedChars === 'numeric'
					? newValue.replaceAll(numbers_dot, '')
					: allowedChars === 'numbers_only'
						? newValue.replaceAll(numbers_only, '')
						: allowedChars === 'alphanumeric'
							? newValue.replaceAll(alphanumeric, '')
							: newValue.replaceAll(numbers_letters_dot_comma, '');
		}

		// Apply transformation before validation
		if (transformation) {
			newValue = transformation(newValue);
		}

		// Validate the transformed value
		if (!newValue.trim() || (validation && validation(newValue.trim()))) setValid(true);
		else if (validation && !validation(newValue.trim())) setValid(false);

		setCurrentValue(newValue);

		if (onChange) {
			onChange(newValue);
		}
	};

	const clearTextarea = (e: React.MouseEvent<HTMLButtonElement>) => {
		e.stopPropagation();
		e.preventDefault();
		setCurrentValue('');
		setValid(true);
		textareaEl.current?.focus();

		if (onChange) onChange('');
		if (onSubmit) onSubmit('');
	};

	const handlePaste = (e: React.ClipboardEvent) => {
		if (!allowPaste) {
			e.preventDefault();
		}
	};

	const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
		if (onKeyDown) onKeyDown(event);
		else if (event.key === 'Enter' && (event.ctrlKey || event.metaKey) && onSubmit) {
			onSubmit(currentValue);
		}
	};

	return (
		<div className={classNames(containerClassName, 'space-y-1')}>
			{title && <h3 className="font-medium tracking-tight text-neutral-300">{title}</h3>}
			<div
				data-disabled={disabled || false}
				className={classNames(
					'flex rounded-lg justify-between gap-1 p-0 px-3 py-2 data-[disabled=true]:pointer-events-none data-[disabled=true]:opacity-60',
					{
						'!ring-2 !ring-red-400 !border-red-400': !isValid,
						'!pointer-events-none !select-none': readonly,
						'overflow-hidden bg-neutral-1000 light:bg-neutral-900/70 has-[:focus]:[box-shadow:inset_0_0_0_1.5px_hsl(var(--twc-neutral-800))]': !floatingLabel,
						'relative bg-neutral-925 light:bg-neutral-950 border border-neutral-800 has-[:focus]:outline-none has-[:focus]:ring-2 has-[:focus]:ring-primary-500 has-[:focus]:border-primary-500 transition-all': floatingLabel
					},
					className
				)}
				style={{ minHeight: floatingLabel ? '3rem' : '2.5rem' }}
			>
				<div className="flex w-full gap-1">
					{Icon && onSubmit && <div className="shrink-0 cursor-pointer self-start pt-1" onClick={() => onSubmit(currentValue)}>{Icon}</div>}
					{Icon && !onSubmit && <div className="shrink-0 self-start pt-1">{Icon}</div>}

					<textarea
						name={name}
						maxLength={maxLength}
						readOnly={readonly}
						rows={rows}
						className={classNames(
							'peer w-full bg-transparent text-sm outline-none placeholder:text-neutral-400 resize-none',
							{
								'!pointer-events-none !select-none': readonly,
								'!text-base': floatingLabel,
								'resize-both': resize === 'both',
								'resize-horizontal': resize === 'horizontal', 
								'resize-vertical': resize === 'vertical',
								'resize-none': resize === 'none'
							},
							textareaClassName
						)}
						placeholder={placeholder}
						ref={textareaEl}
						value={currentValue}
						onChange={handleOnChange}
						onPaste={handlePaste}
						onKeyDown={handleKeyDown}
						tabIndex={readonly ? -1 : undefined}
					/>
				</div>

				{floatingLabel && (
					<label className="absolute -top-2 left-2 px-1 text-xs font-medium text-primary-200/80 bg-neutral-1000 rounded light:text-primary-900 light:bg-primary-300 overflow-hidden overflow-ellipsis text-nowrap max-w-[90%]">
						{floatingLabel}
					</label>
				)}

				{clearable && !disabled && !readonly && (
					<button
						type="button"
						tabIndex={-1}
						onClick={clearTextarea}
						className={classNames('mr-0.5 flex h-6 w-6 shrink-0 self-start mt-0.5 items-center justify-center gap-1', {
							'pointer-events-none opacity-0': !currentValue.trim()
						})}
					>
						<X className="size-[1.125rem]" />
					</button>
				)}
			</div>
		</div>
	);
} 